/**
 * 相册消息（任务 14.1）纯逻辑：ext 相册字段解析 + 网格布局计算。
 *
 * 协议口径（legacy im_msg_parser.dart 实读）：相册信息走 ext JSON——
 * `{"album_id":"<uuid>","album_index":0,"album_total":3}`（解析兼容 camelCase）。
 * 同一 album_id 的**相邻**图片消息在时间线上合并为一个网格气泡（合并在
 * messageModel.buildTimelineItems，按相邻性+同发送者，不依赖 album_index——
 * 与旧版 _messageRunSegments 同口径：中间被别的消息隔断就分成两段）。
 * 发送侧每 5 张切一段（ALBUM_CHUNK_SIZE，旧版 _maxAlbumImageCount 同值），
 * 单张不带相册字段。
 *
 * 布局是**自研等格网格**（刻意不抄旧版 album_layout.dart——那是 Telegram-Android
 * GPL 代码的移植，PROMPT 禁止复制）：
 * - 单张行：整宽，高按宽高比钳 [0.8, 1.8]（未知尺寸按 1:1）；
 * - 多张行：等宽方格，object-fit: cover 裁切——可预测、测高稳定，不做马赛克
 *   纵横比装箱；
 * - 行拆分：2=[2]、3=[1,2]、4=[2,2]、5=[2,3]；≥6（防御性，正常每段 ≤5）每行
 *   3 个、余 1 时末两行改 [2,2]。
 */

/** 发送侧相册分段上限（旧版 _maxAlbumImageCount 同值），14.1b 发送链使用 */
export const ALBUM_CHUNK_SIZE = 5;

/** 相册网格内缝隙（CSS 像素） */
export const ALBUM_GAP = 2;

/** ext → album_id；非相册/解析失败返回空串 */
export function parseAlbumId(ext: string): string {
  if (!ext.startsWith('{')) return '';
  try {
    const r = JSON.parse(ext) as Record<string, unknown>;
    const v = r['album_id'] ?? r['albumId'];
    return typeof v === 'string' ? v.trim() : '';
  } catch {
    return '';
  }
}

/** 发送侧 ext 构造（旧版 buildAlbumExt 同形，snake_case） */
export function buildAlbumExt(albumId: string, albumIndex: number, albumTotal: number): string {
  return JSON.stringify({ album_id: albumId, album_index: albumIndex, album_total: albumTotal });
}

/**
 * 发送侧相册切段（14.1b）：N 张选图 → 每张的 ext（''=不带相册字段按单图发）。
 * 每 ALBUM_CHUNK_SIZE 张一段、段内 ≥2 张才成相册（尾段余 1 张回落单图，
 * 与旧版「单张不带相册字段」同口径）；每段独立 album_id。
 * index/total 按受理前的选图计算——段内个别图片被拒（超限）会留 index 空洞，
 * 渲染合并按相邻性不按 total，空洞只是网格少一格，无需回填。
 */
export function planAlbumExts(count: number, newAlbumId: () => string): string[] {
  const exts: string[] = [];
  for (let start = 0; start < count; start += ALBUM_CHUNK_SIZE) {
    const len = Math.min(ALBUM_CHUNK_SIZE, count - start);
    if (len < 2) {
      exts.push('');
      continue;
    }
    const albumId = newAlbumId();
    for (let i = 0; i < len; i += 1) exts.push(buildAlbumExt(albumId, i, len));
  }
  return exts;
}

export interface AlbumTileRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface AlbumLayout {
  readonly tiles: readonly AlbumTileRect[];
  readonly width: number;
  readonly height: number;
}

/** 行拆分（文件头口径）。n ≥ 2（单张不走相册通道，≥6 防御性兜底） */
function rowCounts(n: number): number[] {
  if (n === 2) return [2];
  if (n === 3) return [1, 2];
  if (n === 4) return [2, 2];
  if (n === 5) return [2, 3];
  const counts: number[] = [];
  let rem = n;
  while (rem > 0) {
    if (rem === 4 || rem === 2) {
      counts.push(2);
      rem -= 2;
    } else if (rem === 1) {
      counts.push(1);
      rem = 0;
    } else {
      counts.push(3);
      rem -= 3;
    }
  }
  return counts;
}

/**
 * 相册网格布局。tiles 与 sizes 一一对应（像素矩形，容器 position:relative +
 * 绝对定位渲染）。整数化坐标保证虚拟列表测高稳定；每行末格宽度取补避免舍入缝。
 */
export function computeAlbumLayout(
  sizes: readonly { readonly width: number; readonly height: number }[],
  opts: { maxWidth: number; gap?: number },
): AlbumLayout {
  const n = sizes.length;
  const W = opts.maxWidth;
  if (n === 0 || W <= 0) return { tiles: [], width: 0, height: 0 };
  const gap = opts.gap ?? ALBUM_GAP;

  const tiles: AlbumTileRect[] = [];
  let top = 0;
  let index = 0;
  for (const count of rowCounts(n)) {
    let rowH: number;
    if (count === 1) {
      const s = sizes[index]!;
      const aspect = s.width > 0 && s.height > 0 ? s.width / s.height : 1;
      rowH = Math.round(W / Math.min(1.8, Math.max(0.8, aspect)));
    } else {
      rowH = Math.round((W - (count - 1) * gap) / count);
    }
    let left = 0;
    for (let i = 0; i < count; i += 1) {
      const isLast = i === count - 1;
      const w = isLast ? W - left : rowH; // 方格：宽=行高；末格取补
      tiles.push({ left, top, width: w, height: rowH });
      left += w + gap;
      index += 1;
    }
    top += rowH + gap;
  }
  return { tiles, width: W, height: top - gap };
}
